<script setup lang="ts">
import { ref } from 'vue'
import { mdiMagnify, mdiClose } from '@mdi/js'
import { vOnClickOutside } from '@vueuse/components'
import { toKebabCase } from '@/shared/utils/format'

import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import BaseButton from '@/shared/components/button/BaseButton.vue'

interface Props {
  title: string
}

interface Emits {
  (e: 'enter'): void
  (e: 'input'): void
  (e: 'clear'): void
  (e: 'open'): void
}

defineOptions({ inheritAttrs: false })
const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<string>({ required: true })
const open = defineModel<boolean>('open', { required: true })
const focus = defineModel<boolean>('focus', { required: true })
const name = toKebabCase(props.title)

const inputRef = ref<HTMLInputElement | null>(null)

const setFocus = (state: boolean) => {
  focus.value = state
}

const onOpen = (): void => {
  setFocus(true)
  emits('open')
}

const onEnter = (): void => {
  setFocus(true)
  emits('enter')
}

const onInput = (): void => {
  setFocus(true)
  emits('input')
}

const onClose = (): void => {
  if (!model.value.length) return

  setFocus(false)
}

const onClear = (): void => {
  model.value = ''
  setFocus(false)
  emits('clear')
}
</script>

<template>
  <div class="relative" :class="{ 'shadow-md': open }" v-on-click-outside="onClose">
    <div class="absolute inset-y-0 flex items-center ms-2">
      <SvgIcon :path="mdiMagnify" class="h-5 w-5" :class="open ? 'fill-gray-700' : 'fill-white'" />
    </div>
    <input
      ref="inputRef"
      :id="name"
      :name="name"
      type="text"
      autocomplete="off"
      spellcheck="false"
      v-model="model"
      class="block placeholder:text-white border text-sm outline-none transition-all duration-200 focus:ring focus:ring-white ease-in-out w-96 ps-8 px-2 py-1"
      :class="[
        open ? 'bg-white border-gray-300 focus:ring-0' : 'bg-black border-gray-800 text-white',
      ]"
      @keydown.enter="onEnter"
      @input="onInput"
      @click="onOpen"
      @focus="inputRef && inputRef.select()"
      v-bind="$attrs"
    />
    <label
      :for="name"
      v-if="!focus && model.length === 0"
      class="pointer-events-none text-sm absolute inset-y-0 start-8 flex items-center"
    >
      <div class="text-white px-1">
        {{ $t('placeholder.search') }}
      </div>
    </label>
    <div class="absolute inset-y-0 end-2 flex items-center ms-4"></div>
    <div v-if="model && model.length > 0" class="absolute inset-y-0 end-2 flex items-center ms-4">
      <BaseButton :title="$t('common.clear')" @click="onClear">
        <SvgIcon :path="mdiClose" class="h-5 w-5" :class="open ? 'fill-gray-700' : 'fill-white'" />
      </BaseButton>
    </div>
  </div>
</template>
